import { getErrorMessage } from "@services/api";
import { authService } from "@services/authService";
import { useAuthStore } from "@store/authStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useEffect } from "react";
import toast from "react-hot-toast";

export function useUser() {
  const { isAuthenticated, loggedInEmail, setUser, logout } = useAuthStore();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["user"],
    queryFn: authService.getProfile,
    enabled: isAuthenticated,
    // 5min fresh window keeps profile data current without hammering the API
    // on every focus event (iPad Safari fires focus on lock/unlock/app-switch).
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Auth failures (401/403) won't recover via retry — fail fast.
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) return false;
      }
      // Network/timeout (e.g. Render cold start): retry up to 2 more times.
      return failureCount < 2;
    },
  });

  // Validate that the user from API matches the logged in user
  useEffect(() => {
    if (query.data) {
      // Security check: validate user email matches the one who logged in
      if (loggedInEmail && query.data.email !== loggedInEmail) {
        console.error(
          `[Security] User mismatch detected! Expected: ${loggedInEmail}, Got: ${query.data.email}`,
        );
        toast.error("Session error detected. Please login again.");
        // Clear all query cache before logging out. ProtectedRoute observes
        // isAuthenticated and redirects via React Router — no hard reload.
        queryClient.clear();
        logout();
        return;
      }

      // Update stored user with fresh data
      console.log(`[Auth] User data loaded for: ${query.data.email}`);
      setUser(query.data);
    }
  }, [query.data, loggedInEmail, setUser, logout, queryClient]);

  return query;
}

// Custom hook for logout that clears React Query cache
export function useLogout() {
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();

  return useCallback(() => {
    console.log("[Auth] Logging out - clearing all cache");
    // Clear all React Query cache
    queryClient.clear();
    // Clear auth store
    logout();
  }, [logout, queryClient]);
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: authService.updateProfile,
    onSuccess: (data) => {
      setUser(data);
      queryClient.invalidateQueries({ queryKey: ["user"] });
      toast.success("Profile updated successfully!");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({
      oldPassword,
      newPassword,
      newPasswordConfirm,
    }: {
      oldPassword: string;
      newPassword: string;
      newPasswordConfirm: string;
    }) =>
      authService.changePassword(oldPassword, newPassword, newPasswordConfirm),
    onSuccess: () => {
      toast.success("Password changed successfully!");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
